import { useEffect, useState } from "react";

/**
 * The Android app (an APK, not on Google Play): offered to an Android phone using Brocoded in a browser tab, and kept up to date from inside
 * the app. The list of builds is the website's `releases.json`, the same one the download page and the Mac app read.
 */
const RELEASES = "https://brocoded.cloud/releases.json";
const GET_KEY = "brocoded.android-card-dismissed";
const UPDATE_KEY = "brocoded.android-update-dismissed";

export interface AndroidRelease {
  version: string;
  url: string;
  size: number;
}

const native = (): any => {
  const cap = (window as any).Capacitor;
  return cap?.isNativePlatform?.() ? cap : null;
};
export const isAndroidBrowser = (): boolean => !native() && /Android/i.test(navigator.userAgent);
export const isAndroidApp = (): boolean => !!native() && /Android/i.test(navigator.userAgent);

/** 0.2.10 is newer than 0.2.9: compare the numbers, not the text. */
export function isNewer(a: string, than: string): boolean {
  const x = a.split(".").map((n) => parseInt(n, 10) || 0);
  const y = than.split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    const l = x[i] ?? 0;
    const r = y[i] ?? 0;
    if (l !== r) return l > r;
  }
  return false;
}

let latest: Promise<AndroidRelease | null> | undefined;
/** The newest Android build the website lists (https only), or null. Asked once per page load. */
export function latestAndroid(fetchImpl: typeof fetch = fetch): Promise<AndroidRelease | null> {
  latest ??= fetchImpl(RELEASES, { cache: "no-cache" })
    .then((r) => (r.ok ? r.json() : null))
    .then((j: { android?: { version?: string; url?: string; size?: number } } | null) => {
      const a = j?.android;
      return a?.version && a.url?.startsWith("https://") ? { version: a.version, url: a.url, size: Number(a.size) || 0 } : null;
    })
    .catch(() => null);
  return latest;
}

/** The version of the installed Android app (its own number, not the web page's). */
async function installedVersion(): Promise<string | null> {
  try {
    const info = await native()?.Plugins?.App?.getInfo?.();
    return typeof info?.version === "string" ? info.version : null;
  } catch {
    return null;
  }
}

const read = (k: string): string | null => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* shown again next time: fine */
  }
};

export type AndroidOffer = { kind: "get"; release: AndroidRelease } | { kind: "update"; release: AndroidRelease; current: string } | null;

/** What to offer this phone right now: the app (browser tab), an update (inside the app), or nothing. */
export function useAndroidOffer(): [AndroidOffer, () => void] {
  const [offer, setOffer] = useState<AndroidOffer>(null);
  useEffect(() => {
    let live = true;
    void (async () => {
      if (isAndroidBrowser() && !read(GET_KEY)) {
        const r = await latestAndroid();
        if (live && r) setOffer({ kind: "get", release: r });
      } else if (isAndroidApp()) {
        const [r, current] = await Promise.all([latestAndroid(), installedVersion()]);
        if (live && r && current && isNewer(r.version, current) && read(UPDATE_KEY) !== r.version) setOffer({ kind: "update", release: r, current });
      }
    })();
    return () => {
      live = false;
    };
  }, []);
  const dismiss = () => {
    if (offer?.kind === "get") write(GET_KEY, "1");
    if (offer?.kind === "update") write(UPDATE_KEY, offer.release.version);
    setOffer(null);
  };
  return [offer, dismiss];
}

/** Download the APK. In the app, hand it to the system browser (the WebView can't download); Android then offers to install the file. */
export function downloadApk(url: string): void {
  if (native()) window.open(url, "_system");
  else location.href = url;
}

const mb = (n: number) => (n ? ` (${Math.max(1, Math.round(n / 1048576))} MB)` : "");

/** A quiet card under the header: get the Android app, or update it. */
export function AndroidAppCard() {
  const [offer, dismiss] = useAndroidOffer();
  if (!offer) return null;
  const get = offer.kind === "get";
  return (
    <div className="banner-wrap" data-android-card={offer.kind}>
      <div className="banner" role="note" style={{ display: "flex", flexDirection: "column", gap: 8, padding: 14 }}>
        <strong>{get ? "Get the Brocoded Android app" : `Brocoded ${offer.release.version} is available`}</strong>
        <span className="fine" style={{ textAlign: "left" }}>
          {get
            ? `One-tap Google sign-in with your phone’s own account, and alerts that arrive reliably. Download it${mb(offer.release.size)}, open the file, and allow installs from this browser if Android asks.`
            : `You have ${offer.current}. Download the update${mb(offer.release.size)} and open the file to install it over this app. Your computers and sign-in stay.`}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn primary sm" onClick={() => downloadApk(offer.release.url)}>
            {get ? "Download" : "Update"}
          </button>
          <button className="btn secondary sm" onClick={dismiss}>
            {get ? "Not now" : "Later"}
          </button>
        </div>
      </div>
    </div>
  );
}
